// ─── src/utils/listingUrl.js ─────────────────────────────────────────────────
// THE canonical URL service for public listings. One builder, imported
// everywhere, Aura, RecommendationCard, Premium Cards, Maps, Compare, Search,
// Editorial, Showcases. No other module may construct a listing URL.
//
// The ROUTER (main.jsx parsePath) defines the truth; this helper emits exactly
// what it accepts:
//   /{country}/{region}/{category}/{slug}   canonical 4-segment (preferred, no redirect)
//   /{category}/{slug}                       2-segment fallback — RESOLVES for every
//                                             category except wedding-venues (main.jsx
//                                             line ~800 treats that specific shape as a
//                                             legacy alias and 301s to canonical instead
//                                             of rendering it; every other category's
//                                             2-segment form is a real page, main.jsx
//                                             line ~924). Confirmed 2 Aug 2026: no
//                                             published listing lacks country_slug
//                                             (Population B empty), so this null only
//                                             ever fires on incomplete caller data, never
//                                             on a genuinely regionless real listing.
// where {category} is a canonical top-level registry slug (wedding-venues,
// wedding-planners, photographers, florists, bridal-boutiques, …).
//
// Category values are normalised through the registry, so aliases and singular
// listingTypes ('venue', 'planner', 'photographer', 'boutique') are corrected to
// their canonical slug automatically, the class of bug that produced
// /planner/{slug} and /vendors/{slug} can no longer reach a URL.
//
// Validation (fail safe, not silently): a URL is only built from a real slug and
// a category the router recognises. Missing slug/category logs in development and
// returns null. A wedding-venues listing with no country_slug logs unconditionally
// (production included) and returns null, since the router itself never resolves that
// shape as a page. Either way, callers hide the link instead of pointing at a broken
// route.
import { getCanonicalCategorySlug, getCategoryBySlug } from '../data/categoryRegistry.js';

const IS_DEV = typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV;

function warnBroken(reason, listing) {
  if (IS_DEV) {
    // Broken URLs must be visible during development, not after deployment.
    console.warn(`[listingUrl] ${reason}, refusing to build a broken route.`, listing);
  }
}

// Read a field tolerating camelCase + snake_case spellings.
function pick(listing, ...keys) {
  for (const k of keys) {
    const v = listing[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return null;
}

/**
 * The Navigation Contract. Normalises any listing-shaped object (DB row, Aura
 * recommendation, card model) into one canonical identity. Nobody calculates
 * URLs field-by-field anymore, they ask for the identity's canonical_url.
 *
 * @returns {{id, slug, category_slug, country_slug, region_slug, canonical_url}|null}
 */
export function listingIdentity(listing) {
  if (!listing) return null;

  const slug = pick(listing, 'slug');
  const rawCategory =
    pick(listing, 'category_slug', 'categorySlug', 'category') ||
    (pick(listing, 'type') === 'venue' ? 'wedding-venues' : null);
  const category_slug = rawCategory ? getCanonicalCategorySlug(rawCategory) : null;
  const country_slug = pick(listing, 'country_slug', 'countrySlug');
  const region_slug = pick(listing, 'region_slug', 'regionSlug');

  const canonical_url = buildCanonicalUrl({ slug, category_slug, country_slug, region_slug }, listing);

  return {
    id: pick(listing, 'id'),
    slug,
    category_slug,
    country_slug,
    region_slug,
    canonical_url,
  };
}

// Internal, the single place a listing path string is assembled.
function buildCanonicalUrl({ slug, category_slug, country_slug, region_slug }, listing) {
  if (!slug) {
    warnBroken('missing slug', listing);
    return null;
  }
  // Category must be a canonical top-level slug the router grid recognises.
  if (!category_slug || !getCategoryBySlug(category_slug)) {
    warnBroken(`unroutable category "${category_slug}"`, listing);
    return null;
  }
  // region === country is not a real region tier (Gibraltar the city-state; an
  // "italy/italy" data slip). The sitemap's listingPath() and the edge 301 both collapse
  // it to the 3-segment form — this function did not, so it emitted a canonical pointing
  // at /gibraltar/gibraltar/... which 301s straight back to the page that advertised it.
  // A page whose canonical redirects to itself is incoherent to a crawler, and this is
  // THE canonical-URL function, so the collapse belongs here rather than in each caller.
  if (country_slug && region_slug && region_slug !== country_slug) {
    return `/${country_slug}/${region_slug}/${category_slug}/${slug}`;
  }
  if (country_slug) {
    return `/${country_slug}/${category_slug}/${slug}`;
  }
  // wedding-venues is the one category where main.jsx's router does NOT resolve the
  // bare 2-segment shape as a page — it 301s it as a legacy alias (parsePath ~line 800).
  // Emitting that shape here would build a link to a route the router itself redirects
  // away from. Every other category's 2-segment form is a real, router-resolved page
  // (parsePath ~line 924), so it still resolves below. Population B (a published listing
  // with no country_slug at all) was confirmed empty on 2 Aug 2026, so this branch firing
  // for wedding-venues always means the CALLER omitted location data that exists in the
  // DB, not that the venue is genuinely regionless — logged unconditionally, not just in
  // dev, because a caller shipping this in production is the exact bug this file exists
  // to prevent.
  if (category_slug === 'wedding-venues') {
    // eslint-disable-next-line no-console
    console.error(`[listingUrl] wedding-venues listing "${slug}" built with no country_slug — caller has incomplete data, refusing to emit a route the router 301s away from.`, listing);
    return null;
  }
  return `/${category_slug}/${slug}`;
}

/**
 * THE listing-URL function. Import this everywhere.
 * Returns the canonical public profile URL, or null when the listing cannot
 * produce a valid route (caller should hide the link / disable navigation).
 */
export function listingProfileUrl(listing) {
  const identity = listingIdentity(listing);
  return identity ? identity.canonical_url : null;
}
